import { useMemo, useRef, useEffect } from "react";
import { DoubleSide } from "three";

export default function FloatProfileSegment({
  totalWidth,
  totalHeight,
  profileWidth,
  profileDepth,
  rotation,
  frontMaterial,
  sideMaterial,
  innerDepthOverride,
  innerWidthOverride,
}) {
  const segmentFrontRef = useRef();
  const sementSideRef = useRef();

  const innerDepth = innerDepthOverride ? innerDepthOverride : 0.8;
  const innerWidth = innerWidthOverride ? innerWidthOverride : 2;

  const segmentFront = useMemo(() => {
    return createSegmentFrontGeometry(
      totalWidth / 2,
      totalHeight / 2,
      profileWidth,
      profileDepth,
      innerWidth,
      innerDepth,
    );
  }, [totalWidth, totalHeight, profileWidth, profileDepth]);

  const segmentFrontUV = useMemo(() => {
    return createSegmentFrontUV(
      totalWidth / 2,
      totalHeight / 2,
      profileWidth,
      profileDepth,
      innerWidth,
      innerDepth,
    );
  }, [totalWidth, totalHeight, profileWidth, profileDepth]);

  const segmentSide = useMemo(() => {
    return createSegmentSideGeometry(
      totalWidth / 2,
      totalHeight / 2,
      profileWidth,
      profileDepth,
      innerWidth,
      innerDepth,
    );
  }, [totalWidth, totalHeight, profileWidth, profileDepth]);

  const segmentSideUV = useMemo(() => {
    return createSegmentSideUV(
      totalWidth / 2,
      totalHeight / 2,
      profileWidth,
      profileDepth,
      innerWidth,
      innerDepth,
    );
  }, [totalWidth, totalHeight, profileWidth, profileDepth]);


  useEffect(() => {
    sementSideRef.current.computeVertexNormals();

    if (sementSideRef.current) {
      sementSideRef.current.attributes.position.needsUpdate = true;
      sementSideRef.current.attributes.uv.needsUpdate = true;
    }
  }, [totalWidth, totalHeight, profileWidth, profileDepth]);


  useEffect(() => {
    segmentFrontRef.current.computeVertexNormals();

    if (segmentFrontRef.current) {
      segmentFrontRef.current.attributes.position.needsUpdate = true;
      segmentFrontRef.current.attributes.uv.needsUpdate = true;
    }
  }, [totalWidth, totalHeight, profileWidth, profileDepth]);

  return (
    <>
      <mesh castShadow rotation-z={rotation} material={frontMaterial} frustumCulled={false}>
        <bufferGeometry ref={segmentFrontRef}>
          <bufferAttribute
            attach="attributes-position"
            count={18}
            itemSize={3}
            array={segmentFront}
          />
          <bufferAttribute
            attach="attributes-uv"
            count={18}
            itemSize={2}
            array={segmentFrontUV}
          />
        </bufferGeometry>
      </mesh>

      <mesh castShadow rotation-z={rotation} material={sideMaterial} frustumCulled={false}>
        <bufferGeometry ref={sementSideRef}>
          <bufferAttribute
            attach="attributes-position"
            count={18}
            itemSize={3}
            array={segmentSide}
          />
          <bufferAttribute
            attach="attributes-uv"
            count={18}
            itemSize={2}
            array={segmentSideUV}
          />
        </bufferGeometry>
      </mesh>

    </>
  );
}

function createSegmentFrontGeometry(w, h, pW, pD, iW, iD) {
  const vertices = new Float32Array([
    -(w + 0.3 + pW),    h + 0.3 + pW,    pD, // 0
    w + 0.3 + pW,       h + 0.3 + pW,    pD, // 1
    w + 0.3,            h + 0.3,         pD, // 2

    -(w + 0.3 + pW),    h + 0.3 + pW,    pD, // 3
    w + 0.3,            h + 0.3,         pD, // 4
    -(w + 0.3),         h + 0.3,         pD, // 5

    -(w + 0.3),         h + 0.3,          pD - iD, // 6
    w + 0.3,            h + 0.3,          pD - iD, // 7
    w - iW,              h - iW,            pD - iD, // 8

    -(w + 0.3),         h + 0.3,          pD - iD, // 9
    w - iW,              h - iW,            pD - iD, // 10
    -(w - iW),           h - iW,            pD - iD, // 11

    w - iW,              h - iW,            0, // 12
    -(w - iW),           h - iW,            0, // 13
    w + 0.3 + pW,       h + 0.3 + pW,     0, // 14

    -(w - iW),           h - iW,            0, // 15
    -(w + 0.3 + pW),    h + 0.3 + pW,     0, // 16
    w + 0.3 + pW,       h + 0.3 + pW,     0, // 17
  ]);

  return vertices;
}

function createSegmentFrontUV(w, h, pW, pD, iW, iP) {
  const uv = new Float32Array([
    -w - pW,    pW,      // 0
    w + pW,     pW,     // 1
    w,          0,      // 2

    -w - pW,    pW,     // 5
    w,          0,     // 4
    -w,         0,      // 3

    -w - 0.3,   2.3,    // 6
    w  + 0.3,   2.3,    // 7
    w - iW,      0,      // 8

    -w - 0.3,   2.3,    // 9
    w - iW,      0,    // 10
    -w + iW,     0,    // 11

    -w,             0, // 12
    w,              0, // 13
    w,              0.6, // 14

    -w,             0, // 15
    w,              0.6, // 16
    -w,             0.6, // 17
  ]);

  return uv;
}


function createSegmentSideGeometry(w, h, pW, pD, iW, iP) {
  const vertices = new Float32Array([
    w + 0.3 + pW,       h + 0.3 + pW,    0, // 0
    -(w + 0.3 + pW),    h + 0.3 + pW,    0, // 1
    w + 0.3 + pW,       h + 0.3 + pW,    pD, // 2

    -(w + 0.3 + pW),    h + 0.3 + pW,    0, // 3
    w + 0.3 + pW,       h + 0.3 + pW,    pD, // 4
    -(w + 0.3 + pW),    h + 0.3 + pW,    pD, // 5

    w + 0.3,            h + 0.3,          pD, // 6    
    -(w + 0.3),         h + 0.3,          pD, // 7
    w + 0.3,            h + 0.3,          pD - iP, // 8

    -(w + 0.3),         h + 0.3,          pD, // 9
    w + 0.3,            h + 0.3,          pD - iP, // 10
    -(w + 0.3),         h + 0.3,          pD - iP, // 11

    w - iW,              h - iW,            pD - iP, // 12
    -(w - iW),           h - iW,            pD - iP, // 13
    w - iW,              h - iW,            0, // 14

    -(w - iW),           h - iW,            pD - iP, // 15
    w - iW,              h - iW,            0, // 16
    -(w - iW),           h - iW,            0, // 17
  ]);

  return vertices;
}

function createSegmentSideUV(width, height, pWidth, pDepth, iW, iP) {
  const uv = new Float32Array([
    width + 0.3 + pWidth,             0, // 0
    -width - 0.3 - pWidth,              0, // 1
    width + 0.3 + pWidth,     pDepth, // 2

    -width - 0.3 - pWidth,             0, // 3
    width + 0.3 + pWidth,     pDepth, // 4
    -width - 0.3 - pWidth,    pDepth, // 5

    width / 2 + pWidth,    pDepth, // 6
    -width / 2 - pWidth,     pDepth, // 7
    width / 2 + pWidth,     pDepth - iP, // 8

    -width / 2 - pWidth,    pDepth, // 9
    width / 2 + pWidth,     pDepth - iP, // 10
    -width / 2 - pWidth,    pDepth - iP, // 11

    width - 2,             pDepth - iP, // 12
    -width + 2,              pDepth - iP, // 13
    width - 2,              0, // 14

    -width + 2,             pDepth - iP, // 15
    width - 2,              0, // 16
    -width + 2,             0, // 17
  ]);

  return uv;
}